import React, { useState } from 'react';
import styled from 'styled-components';
import MobileDock from './MobileDock';
import MobileWindowManager from './MobileWindowManager';
import { useWindows } from '../../contexts/WindowContext';
import AboutApp from '../Applications/AboutApp/AboutApp';
import SkillsMonitor from '../Applications/SkillsMonitor/SkillsMonitor';
import ProjectsExplorer from '../Applications/ProjectsExplorer/ProjectsExplorer';
import HackerGames from '../Applications/HackerGames/HackerGames';
import YouTubeChannel from '../Applications/YouTubeChannel/YouTubeChannel';
import ContactNetwork from '../Applications/ContactNetwork/ContactNetwork';
import TerminalApp from '../Applications/Terminal/TerminalApp';
import EducationSystem from '../Applications/EducationSystem/EducationSystem';
import AchievementsCenter from '../Applications/AchievementsCenter/AchievementsCenter';

const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%);
  overflow: hidden;
  position: relative;
`;

const MobileTopBar = styled.div`
  height: 60px;
  background: rgba(0, 0, 0, 0.95);
  color: #00ff00;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  border-bottom: 2px solid rgba(0, 255, 0, 0.3);
  font-size: 14px;
  font-family: 'Ubuntu', sans-serif;
  backdrop-filter: blur(10px);
`;



const WelcomeSection = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const WelcomeTitle = styled.h1`
  color: #00ffff;
  font-size: 24px;
  margin-bottom: 10px;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.5);
`;

const WelcomeSubtitle = styled.p`
  color: #cccccc;
  font-size: 14px;
  margin-bottom: 20px;
`;

const QuickAccessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 30px;
`;

const QuickAccessCard = styled.div`
  background: rgba(0, 255, 0, 0.1);
  border: 2px solid rgba(0, 255, 0, 0.3);
  border-radius: 15px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  &:active {
    transform: scale(0.95);
    background: rgba(0, 255, 0, 0.2);
    border-color: #00ff00;
  }
`;

const CardIcon = styled.div`
  font-size: 32px;
  margin-bottom: 8px;
`;

const CardTitle = styled.div`
  color: #00ff00;
  font-size: 12px;
  font-weight: bold;
`;

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  font-size: 12px;
`;

const BatteryIcon = styled.div<{ level: number }>`
  width: 24px;
  height: 12px;
  border: 1px solid #00ff00;
  border-radius: 2px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    right: -3px;
    width: 2px;
    height: 6px;
    background: #00ff00;
    border-radius: 0 1px 1px 0;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 1px;
    left: 1px;
    width: ${props => (props.level / 100) * 20}px;
    height: 8px;
    background: ${props => props.level > 20 ? '#00ff00' : '#ff0000'};
    border-radius: 1px;
  }
`;

const StatsSection = styled.div`
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
`;

const StatsTitle = styled.h3`
  color: #00ffff;
  margin-bottom: 15px;
  font-size: 16px;
`;

const StatItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  color: #cccccc;
  font-size: 12px;
`;

const StatValue = styled.span`
  color: #00ff00;
  font-weight: bold;
`;

const MobileWallpaper = styled.div`
  flex: 1;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%);
  position: relative;
  padding: 30px 20px 100px 20px; /* Add bottom padding to account for dock */
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;



const MobileDesktop: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const { openWindow } = useWindows();

  React.useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const quickAccessApps = [
    { 
      name: 'About', 
      icon: '👤', 
      component: AboutApp,
      title: 'About Mukul Joshi'
    },
    { 
      name: 'Skills', 
      icon: '⚡', 
      component: SkillsMonitor,
      title: 'Skills Monitor'
    },
    { 
      name: 'Projects', 
      icon: '📁', 
      component: ProjectsExplorer,
      title: 'Projects Explorer'
    },
    { 
      name: 'Games', 
      icon: '🎮', 
      component: HackerGames,
      title: 'Hacker Games'
    },
    { 
      name: 'YouTube', 
      icon: '📺', 
      component: YouTubeChannel,
      title: 'CYB3RFY YouTube Channel'
    },
    { 
      name: 'Contact', 
      icon: '📞', 
      component: ContactNetwork,
      title: 'Contact Network'
    }
  ];

  const handleAppOpen = (app: typeof quickAccessApps[0]) => {
    openWindow({
      title: app.title,
      component: app.component,
      isMinimized: false,
      isMaximized: false,
      position: { x: 0, y: 0 },
      size: { width: window.innerWidth, height: window.innerHeight }
    });
  };

  return (
    <MobileContainer>
      <MobileTopBar>
        <div>🐧 Mukul's Portfolio</div>
        <StatusBar>
          <span>📶</span>
          <BatteryIcon level={87} />
          <span>{currentTime.toLocaleTimeString()}</span>
        </StatusBar>
      </MobileTopBar>
      
      <MobileWallpaper>
        <WelcomeSection>
          <WelcomeTitle>Welcome to My Portfolio</WelcomeTitle>
          <WelcomeSubtitle>Cybersecurity Professional & Ethical Hacker</WelcomeSubtitle>
        </WelcomeSection>

        <QuickAccessGrid>
          {quickAccessApps.map((app, index) => (
            <QuickAccessCard key={index} onClick={() => handleAppOpen(app)}>
              <CardIcon>{app.icon}</CardIcon>
              <CardTitle>{app.name}</CardTitle>
            </QuickAccessCard>
          ))}
        </QuickAccessGrid>

        
      </MobileWallpaper>
      
      <MobileWindowManager />
      <MobileDock />
    </MobileContainer>
  );
};

export default MobileDesktop;
